import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import SiteFooter from './SiteFooter';

/* Three yoyos on shrinking strings, climbing like a bar chart. */
const YOYOS = [
  { x: 28, y: 68, c: '#6E8A33' },
  { x: 50, y: 54, c: '#97BF40' },
  { x: 72, y: 40, c: 'var(--lime)' },
];

function Logo() {
  return (
    <Link to="/" className="logo" aria-label="StatYoyo home">
      <span className="tile">
        <svg viewBox="0 0 100 100" aria-hidden="true">
          {YOYOS.map(({ x, y, c }) => (
            <g key={x}>
              <path d={`M${x} 20 V${y - 11}`} stroke={c} strokeWidth="3.5" strokeLinecap="round" />
              <circle cx={x} cy={y} r="11" fill={c} />
              <circle cx={x} cy={y} r="3.8" fill="var(--base)" />
            </g>
          ))}
        </svg>
      </span>
      <span className="wm">stat<span className="yo">yoyo</span></span>
    </Link>
  );
}

/* One site, one portal per game. The game is read from the URL, so every
   existing link keeps working; the nav and the mobile tab bar show only the
   current game's pages, plus a switch to the other game. */
const isClashUpgrades = (p) =>
  p.startsWith('/clash') && !p.startsWith('/clash/heroes') && !p.startsWith('/clash/evolutions')
  && !p.startsWith('/clash/decks') && !p.startsWith('/clash/meta') && !p.startsWith('/clash/trending');

// Both games' news lives on one page. Top nav only: the mobile tab bar is full.
const NEWS = { to: '/news', label: 'News', icon: '📰', match: (p) => p.startsWith('/news'), tab: false };

const GAMES = [
  {
    key: 'brawl',
    label: 'Brawl Stars',
    short: 'Brawl',
    icon: '◆',
    home: '/',
    links: [
      { to: '/', label: 'Dashboard', icon: '◆', match: (p) => p === '/' },
      { to: '/brawlers', label: 'Tier List', icon: '▦', match: (p) => p.startsWith('/brawlers') },
      { to: '/maps', label: 'Maps', icon: '🗺', match: (p) => p.startsWith('/maps') },
      { to: '/meta-report', label: 'Report', icon: '📰', match: (p) => p.startsWith('/meta-report') },
      { to: '/players', label: 'Players', icon: '👤', match: (p) => p.startsWith('/players') },
      NEWS,
    ],
  },
  {
    key: 'clash',
    label: 'Clash Royale',
    short: 'Clash',
    icon: '♛',
    home: '/clash/decks',
    links: [
      { to: '/clash/decks', label: 'Decks', icon: '🂠', match: (p) => p.startsWith('/clash/decks') },
      { to: '/clash/trending', label: 'Trending', icon: '↗', match: (p) => p.startsWith('/clash/trending') },
      { to: '/clash/meta', label: 'Meta', icon: '◎', match: (p) => p.startsWith('/clash/meta') },
      { to: '/clash', label: 'Upgrades', icon: '⬆', match: isClashUpgrades },
      { to: '/clash/heroes', label: 'Heroes', icon: '★', match: (p) => p.startsWith('/clash/heroes') || p.startsWith('/clash/evolutions') },
      NEWS,
    ],
  },
];

const gameFor = (p) => (p.startsWith('/clash') ? GAMES[1] : GAMES[0]);

function Layout({ children }) {
  const { pathname } = useLocation();
  const game = gameFor(pathname);
  const other = GAMES.find((g) => g !== game);

  return (
    <div className="app">
      <nav className="nav">
        <div className="wrap">
          <Logo />
          <div className="game-switch" role="tablist" aria-label="Game">
            {GAMES.map((g) => (
              <Link key={g.key} to={g.home} role="tab" aria-selected={g === game} className={g === game ? 'on' : ''}>
                <span className="gs-long">{g.label}</span><span className="gs-short">{g.short}</span>
              </Link>
            ))}
          </div>
          <div className="nav-links">
            {game.links.map((l) => (
              <Link key={l.to} to={l.to} className={l.match(pathname) ? 'on' : ''}>
                {l.label}
              </Link>
            ))}
          </div>
          <span className="spacer" />
          {game.key === 'brawl' && <Link to="/players" className="search">🔎 Find a player…</Link>}
          <div className="league-pill">Global<span className="tag">meta</span></div>
        </div>
      </nav>

      {children}

      <SiteFooter />

      {/* Mobile: fixed bottom tab bar (the top links are hidden < 860px) */}
      <nav className="tabbar">
        {game.links.filter((l) => l.tab !== false).map((l) => (
          <Link key={l.to} to={l.to} className={l.match(pathname) ? 'on' : ''}>
            <span className="tb-ic">{l.icon}</span>
            <span className="tb-l">{l.label}</span>
          </Link>
        ))}
        <Link to={other.home} className="tb-switch">
          <span className="tb-ic">{other.icon}</span>
          <span className="tb-l">{other.short}</span>
        </Link>
      </nav>
    </div>
  );
}

export default Layout;
